import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import AutoResizeTextarea from "./AutoResizeTextarea";
import './ObjectiveCard.css';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMemberSearch } from "../../hooks/useMemberSearch";

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DraggableInitiative from "./DraggableInitiative";

// ── KR Champion autocomplete ───────────────────────────────────────────────────
const ChampionPicker = ({ value, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const selectedFromDropdown = useRef(false);
  const suggestions = useMemberSearch(query === value ? "" : query);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    if (suggestions.length > 0) setOpen(true);
  }, [suggestions]);

  const handleSelect = (member) => {
    selectedFromDropdown.current = true;
    setQuery(member.full_name);
    setOpen(false);
    onSelect(member);
  };

  const handleClear = () => {
    setQuery("");
    setOpen(false);
    onSelect(null);
  };

  // Якщо користувач вийшов з поля без вибору зі списку — зберігаємо як вільний текст
  const handleBlur = () => {
    setOpen(false);
    if (!selectedFromDropdown.current) {
      onSelect({ id: null, full_name: query.trim() });
    }
    selectedFromDropdown.current = false;
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div className="bubble" style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px" }}>
        <input
          type="text"
          value={query}
          placeholder={t('objective_card.champion_placeholder')}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={handleBlur}
          autoComplete="off"
          className="no-arrows-input"
          style={{ border: "none", outline: "none", background: "transparent", width: 100, fontSize: "inherit" }}
        />
        {query && (
          <button onClick={handleClear} style={{ background: "none", border: "none", cursor: "pointer", lineHeight: 1, padding: 0, opacity: 0.5, fontSize: "20px" }}>×</button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% - 4px)", left: 0, minWidth: 200,
          background: "var(--base-color, #fff)", border: "none",
          borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 500, overflow: "hidden"
        }}>
          {suggestions.map(m => (
            <button
              key={m.id}
              onMouseDown={() => handleSelect(m)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start",
                width: "100%", padding: "10px 16px", background: "none", border: "none",
                cursor: "pointer", textAlign: "left", gap: 3
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--transparent-hover-color)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--inverse-color)" }}>{m.full_name}</span>
              <span style={{ fontSize: 12, color: "var(--grey-text, #9ca3af)" }}>{m.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Maps stored neutral key (or legacy Ukrainian text) → translated label
const getTypeLabel = (key, t) => {
  const map = {
    'ambitious': t('objective_card.type_ambitious'),
    'committed': t('objective_card.type_committed'),
    'learning':  t('objective_card.type_learning'),
    // legacy values stored before normalization
    'Амбітна ціль':    t('objective_card.type_ambitious'),
    "Обов'язкова ціль": t('objective_card.type_committed'),
    'Навчальна ціль':  t('objective_card.type_learning'),
    'Ambitious goal':  t('objective_card.type_ambitious'),
    'Committed goal':  t('objective_card.type_committed'),
    'Learning goal':   t('objective_card.type_learning'),
  };
  return map[key] ?? key;
};

const ObjectiveCard = ({ objective, updateObjective, deleteObjective, resetSignal }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(objective.title);
  const [weight, setWeight] = useState(objective.weight);
  const [type, setType] = useState(objective.type);
  const [keyResults, setKeyResults] = useState(objective.key_results || []);
  const [activeInitiativeIndex, setActiveInitiativeIndex] = useState(null);
  const [, setActiveInitiativeWeight] = useState(0);

  const [weightError, setWeightError] = useState(false);
  const [krErrors, setKrErrors] = useState(
    objective.key_results?.map(() => ({ target: false, weight: false })) || []
  );
  const [krDropdownOpen, setKrDropdownOpen] = useState(false);

  const initiativeRef = useRef(null);

  useEffect(() => {
    setTitle(objective.title);
    setWeight(objective.weight);
    setType(objective.type);
    setKeyResults(JSON.parse(JSON.stringify(objective.key_results)));

  }, [resetSignal, objective]);

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedWeightIcon = event.target.closest('.weight-icon');
      if (clickedWeightIcon) {
        return;
      }
      if (initiativeRef.current && !initiativeRef.current.contains(event.target)) {
        setActiveInitiativeIndex(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setKrErrors((prevErrors) => {
      const newErrors = keyResults.map((_, index) => {
        return prevErrors[index] || { initial_value: false, target_value: false, weight: false };
      });
      return newErrors;
    });
  }, [keyResults]);

  useEffect(() => {
    setWeight(objective.weight);
  }, [objective.weight]);

  const isNumeric = (value) => {
    return /^\d+(\.\d+)?$/.test(value) && Number(value) >= 0;
  };

  const isValidWeight = (value) => {
    const numericPattern = /^(100|[1-9][0-9]?|0)(\.\d+)?$/;

    return numericPattern.test(value);
  };

  const addInitiative = (krIndex) => {
    const newKeyResults = [...keyResults];
    newKeyResults[krIndex].initiatives.push({ title: "", is_complete: false, weight: 1 });
    setKeyResults(newKeyResults);
  };

  const updateKeyResult = (krIndex, newTitle) => {
    const newKeyResults = [...keyResults];
    newKeyResults[krIndex].title = newTitle;
    setKeyResults(newKeyResults);
  };

  const handleUpdate = (objType = type) => {
    const noKrErrors = krErrors.every(
      (err) => !err.initial_value && !err.target_value && !err.weight
    );
    if (!weightError && noKrErrors) {
      updateObjective(objective.id, {
        title,
        weight,
        type: objType,
        key_results: keyResults,
      });
    }
  };

const addKeyResult = (newType) => {
  setKeyResults((prev) => {
    const newKr = {
      title: "",
      deadline: "",
      champion_name: "",
      initiatives: [],
      weight: 0,
      type: newType,
      initial_value: newType === "numeric" ? 0 : null,
      target_value: newType === "numeric" ? 0 : null,
    };
    const next = recalculateKrWeights([...prev, newKr]);

    updateObjective(objective.id, { ...objective, key_results: next });
    setKrErrors([...krErrors, { initial_value: false, target_value: false, weight: false }]);

    return next;
  });
};


  const handleDateChange = (index, newDate) => {
    const newKeyResults = [...keyResults];
    newKeyResults[index].deadline = newDate;
    setKeyResults(newKeyResults);
  };

  const handleChampionSelect = (index, member) => {
    const newKeyResults = [...keyResults];
    newKeyResults[index].champion_name = member ? member.full_name : "";
    newKeyResults[index].champion_user_id = member ? member.id : null;
    setKeyResults(newKeyResults);
    handleUpdate();
  };

   const changeKeyResultWeight = (index, newWeight) => {
    const updated = [...keyResults];
    updated[index].weight = newWeight;
    setKeyResults(updated);

    const updatedErrors = [...krErrors];
    updatedErrors[index].weight = !isValidWeight(newWeight);
    setKrErrors(updatedErrors);
  };

  const changeKeyResultInitialTargetValue = (index, newValue) => {
    const updated = [...keyResults];
    updated[index].initial_value = newValue;
    setKeyResults(updated);

    const updatedErrors = [...krErrors];
    updatedErrors[index].initial_value= !isNumeric(newValue);
    setKrErrors(updatedErrors);
  };

  const changeKeyResultEndTargetValue = (index, newValue) => {
    const updated = [...keyResults];
    updated[index].target_value = newValue;
    setKeyResults(updated);

    const updatedErrors = [...krErrors];
    updatedErrors[index].target_value = !isNumeric(newValue);
    setKrErrors(updatedErrors);
  };

  const deleteKeyResult = (krIndex) => {
  setKeyResults((prev) => {
    const next = recalculateKrWeights(
      prev.filter((_, idx) => idx !== krIndex)
    );

    updateObjective(objective.id, { ...objective, key_results: next });
    setKrErrors((errs) => errs.filter((_, idx) => idx !== krIndex));

    return next;
  });
};

  const toggleKrDropdown = () => {
    setKrDropdownOpen((prev) => !prev);
  };
  
  const moveInitiative = (krIndex, fromIndex, toIndex) => {
    const newKeyResults = [...keyResults];
    const initiatives = newKeyResults[krIndex].initiatives;
    const [movedItem] = initiatives.splice(fromIndex, 1);
    initiatives.splice(toIndex, 0, movedItem);
    newKeyResults[krIndex].initiatives = initiatives;

    setKeyResults(newKeyResults);

    updateObjective(objective.id, {
      title,
      weight,
      type,
      key_results: newKeyResults
    });
  };

 
  const deleteInitiative = (krIndex, initIndex) => {
    const newKeyResults = [...keyResults];
    newKeyResults[krIndex].initiatives = newKeyResults[krIndex].initiatives.filter((_, index) => index !== initIndex);
    setKeyResults(newKeyResults);
    handleUpdate();
  };


  const recalculateKrWeights = (list) => {
    if (!list.length) return list;

    const even = parseFloat((100 / list.length).toFixed(1));

    return list.map((kr) => ({ ...kr, weight: even }));
  };

  const getInputWidth = (value) => {
    if (/^\d\.\d$/.test(String(value))) {
      return '23px';
    }
    
    const digits = String(value).replace(/\D/g, '').length;
    const hasDotAtEnd = String(value).endsWith('.') ? 1 : 0;
    const length = Math.max(digits + hasDotAtEnd, 1.2);

    const multiplier = String(value).includes('.') ? 10.2 : 9.6;

    return `${length * multiplier}px`;
  };

  return (
    <div className="card">
      <div className="card-top">
        <div className="card-top-left">
        <AutoResizeTextarea
          value={title}
          onChange={setTitle}
          onBlur={() => handleUpdate()}
          placeholder={t('objective_card.objective_title_placeholder')}
          className="h2 highlight-on-hover"
        />
        <div className="goal-dropdown-wrapper">
          <div className="goal-dropdown">
            {type ? getTypeLabel(type, t) : t('objective_card.select_type')}
            <img src="data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%276%27 viewBox=%270 0 12 6%27 fill=%27none%27%3E%3Cpath d=%27M1 1L6 5L11 1%27 stroke=%27%2354565A%27 stroke-width=%271.5%27 stroke-linecap=%27round%27/%3E%3C/svg%3E" alt=""/>

          </div>

          <ul className="goal-dropdown-menu">
            <li onClick={() => { setType('ambitious'); handleUpdate('ambitious'); }}>
              {t('objective_card.type_ambitious')}
            </li>
            <li onClick={() => { setType('committed'); handleUpdate('committed'); }}>
              {t('objective_card.type_committed')}
            </li>
            <li onClick={() => { setType('learning'); handleUpdate('learning'); }}>
              {t('objective_card.type_learning')}
            </li>
          </ul>
        </div>
        <Tooltip 
              className="goal-tooltip"
              placement="top"
              title={<div dangerouslySetInnerHTML={{ __html: t('objective_card.type_tooltip') }} />}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'white',
                    color: 'black',
                    boxShadow: 6,
                    fontSize: '14px',
                    borderRadius: '12px',
                    padding: '14px',
                  },
                },
                arrow: {
                  sx: {
                    color: 'white'
                  },
                },
              }}
             >
              <InfoOutlinedIcon fontSize="small" sx={{ marginLeft: '6px', cursor: 'pointer', verticalAlign: 'middle' }} />
            </Tooltip>

          <div className="goal-weight">  
          <p className="bubble number" style={{marginRight: '0'}}>          
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_2_2958" style={{maskType:"alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
              <rect width="20" height="20" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_2_2958)">
              <path d="M16.2907 7.97904L15.5247 7.213L16.0999 6.63779C16.1534 6.58432 16.1801 6.52418 16.1801 6.45738C16.1801 6.39057 16.1534 6.3305 16.0999 6.27717L13.7218 3.91988C13.6684 3.8664 13.6084 3.83967 13.5416 3.83967C13.4748 3.83967 13.4146 3.8664 13.3611 3.91988L12.8068 4.49509L12.0407 3.72904L12.6272 3.12175C12.8815 2.86745 13.1876 2.74029 13.5455 2.74029C13.9034 2.74029 14.2095 2.86745 14.4639 3.12175L16.898 5.55592C17.1523 5.81022 17.2795 6.11286 17.2795 6.46384C17.2795 6.81481 17.1523 7.11745 16.898 7.37175L16.2907 7.97904ZM7.26677 17.003C7.10969 17.1601 6.88344 17.2664 6.58802 17.322C6.2926 17.3775 6.01774 17.2781 5.76344 17.0238L3.19469 14.4549C2.93497 14.1995 2.8051 13.8838 2.8051 13.5078C2.8051 13.1318 2.93497 12.814 3.19469 12.5543L3.74906 12.0207L4.5151 12.7868L3.96865 13.3203C3.91517 13.3738 3.88844 13.4339 3.88844 13.5007C3.88844 13.5675 3.91517 13.6276 3.96865 13.6809L6.31802 16.0511C6.37135 16.1046 6.43142 16.1313 6.49823 16.1313C6.56503 16.1313 6.62517 16.1046 6.67865 16.0511L7.23302 15.5047L7.99906 16.2707L7.26677 17.003ZM15.1866 10.9807L16.2955 9.89259C16.349 9.83911 16.3757 9.77765 16.3757 9.70821C16.3757 9.63877 16.349 9.57731 16.2955 9.52384L10.5168 3.74509C10.4633 3.69161 10.4018 3.66488 10.3324 3.66488C10.263 3.66488 10.2015 3.69161 10.148 3.74509L9.03906 4.83321C8.98573 4.88668 8.95906 4.94675 8.95906 5.01342C8.95906 5.08022 8.98573 5.14036 9.03906 5.19384L14.8259 10.9807C14.8794 11.034 14.9395 11.0607 15.0064 11.0607C15.073 11.0607 15.1331 11.034 15.1866 10.9807ZM9.9126 16.2755L11.0007 15.1953C11.0541 15.142 11.0807 15.0819 11.0807 15.0151C11.0807 14.9483 11.0541 14.8882 11.0007 14.8349L5.20573 9.03988C5.1524 8.98654 5.09233 8.95988 5.02552 8.95988C4.95872 8.95988 4.89865 8.98654 4.84531 9.03988L3.7651 10.128C3.71163 10.1815 3.6849 10.2429 3.6849 10.3124C3.6849 10.3818 3.71163 10.4433 3.7651 10.4968L9.54385 16.2755C9.59733 16.329 9.65878 16.3557 9.72823 16.3557C9.79767 16.3557 9.85913 16.329 9.9126 16.2755ZM9.57594 11.8493L11.8822 9.55592L10.4639 8.15842L8.17052 10.4438L9.57594 11.8493ZM10.6705 17.0495C10.4109 17.3092 10.0968 17.439 9.72823 17.439C9.35962 17.439 9.04552 17.3092 8.78594 17.0495L2.99115 11.2338C2.73142 10.9743 2.60156 10.6636 2.60156 10.302C2.60156 9.94029 2.73142 9.62967 2.99115 9.37008L4.07927 8.27384C4.33469 8.01425 4.65038 7.88446 5.02635 7.88446C5.40247 7.88446 5.72031 8.01425 5.9799 8.27384L7.40448 9.67779L9.6899 7.37175L8.2651 5.95988C8.00552 5.70029 7.87573 5.38481 7.87573 5.01342C7.87573 4.64217 8.00552 4.32675 8.2651 4.06717L9.38219 2.97113C9.63761 2.7114 9.9533 2.58154 10.3293 2.58154C10.7052 2.58154 11.023 2.7114 11.2826 2.97113L17.0903 8.73717C17.35 8.99259 17.4799 9.30828 17.4799 9.68425C17.4799 10.0602 17.35 10.378 17.0903 10.6376L15.9734 11.7338C15.7139 11.9934 15.3984 12.1232 15.0272 12.1232C14.6558 12.1232 14.3403 11.9934 14.0807 11.7338L12.6272 10.309L10.342 12.6153L11.7668 14.0399C12.0264 14.2995 12.1596 14.6163 12.1666 14.9903C12.1735 15.3642 12.0472 15.6809 11.7876 15.9405L10.6705 17.0495Z" fill="var(--inverse-color)"/>
              </g>
            </svg>
          <span><input
              style={{ width: getInputWidth(weight) }}
              value={weight}
              placeholder="0"
              onChange={(e) => {
                const val = e.target.value;
                setWeightError(!isValidWeight(val));
                updateObjective(objective.id, { weight: val, isWeightCustomized: true });
              }}
              onBlur={() => handleUpdate()}
            />%</span></p>
            
            <Tooltip 
                className="goal-tooltip"
                placement="top"
                title={<div className="goal-tooltip"><p>{t("objective_card.weight_tooltip_text")}</p></div>}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: 6,
                      fontSize: '14px',
                      borderRadius: '12px',
                      padding: '14px',
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'white', // колір стрілки під тултіпом
                    },
                  },
                }}
                >
                <InfoOutlinedIcon fontSize="small" sx={{ marginLeft: '6px', cursor: 'pointer', verticalAlign: 'middle' }} />
              </Tooltip>  
            </div>  
            {weightError && <p className="form-error">{t("objective_card.weight_error")}</p>}
        </div>
        <div className="completion-circle"><div className="percentage">0%</div></div>
      </div>
      {keyResults.map((kr, index) => (
        <div key={`kr-${kr.id}` || `kr-${index}`} className="kr-container">
          <div className="kr-value-container">
            {/* <div className="progress-bar"></div> */}
            {kr.type === "numeric" && (
              <div className="numeric-inputs">
                <input
                  value={kr.initial_value}
                  placeholder="0"
                  onChange={(e) => changeKeyResultInitialTargetValue(index, e.target.value)}
                  onBlur={() => handleUpdate()}
                  className="bubble initial-value"
                  style={{ width: "auto", marginRight: "10px" }}
                />
                 <div className="numeric-line"></div>
                <input
                  value={kr.target_value}
                  placeholder="0"
                  onChange={(e) => changeKeyResultEndTargetValue(index, e.target.value)}
                  onBlur={() => handleUpdate()}
                  className="bubble"
                  style={{ width: "auto", marginLeft: "10px" }}
                />
              </div>
            )}
          </div>
          {krErrors[index]?.initial_value && (
            <p className="form-error" style={{ textAlign: "right", marginTop: "-10px" }}>
              {t("objective_card.kr_initial_error")}
            </p>
          )}
          {krErrors[index]?.target_value && (
            <p className="form-error" style={{ textAlign: "right", marginTop: "-10px" }}>
              {t("objective_card.kr_target_error")}
            </p>
          )}
          <div className="kr-header">
              <AutoResizeTextarea
                value={kr.title}
                onChange={(newTitle) => updateKeyResult(index, newTitle)}
                onBlur={() => handleUpdate()}
                placeholder={t("objective_card.kr_title_placeholder")}
                className="h3 highlight-on-hover"
              />
              <button className="delete-button" onClick={() => deleteKeyResult(index)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.33333 13.2292L10 11.5625L11.6667 13.2292L12.7292 12.1667L11.0625 10.5L12.7292 8.83333L11.6667 7.77083L10 9.4375L8.33333 7.77083L7.27083 8.83333L8.9375 10.5L7.27083 12.1667L8.33333 13.2292ZM6.5 17C6.0875 17 5.73438 16.8531 5.44062 16.5594C5.14687 16.2656 5 15.9125 5 15.5V5.5H4V4H8V3H12V4H16V5.5H15V15.491C15 15.9137 14.8531 16.2708 14.5594 16.5625C14.2656 16.8542 13.9125 17 13.5 17H6.5ZM13.5 5.5H6.5V15.5H13.5V5.5Z"/>
                </svg>
              </button>
          </div>
          <div className="kr-content">
            <div className="tags">
              <input
                type="date"
                value={kr.deadline || ""}
                placeholder={t("objective_card.deadline_placeholder")}
                onChange={(e) => handleDateChange(index, e.target.value)}
                onBlur={() => handleUpdate()}
                className="bubble"
              />
              <ChampionPicker
                value={kr.champion_name || ""}
                onSelect={(member) => handleChampionSelect(index, member)}
              />
              <div className="bubble kr-weight">
                <div className="kr-weight-container">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="mask0_2_2958" styles={{maskType:"alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                    <rect width="20" height="20" fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#mask0_2_2958)">
                    <path d="M16.2907 7.97904L15.5247 7.213L16.0999 6.63779C16.1534 6.58432 16.1801 6.52418 16.1801 6.45738C16.1801 6.39057 16.1534 6.3305 16.0999 6.27717L13.7218 3.91988C13.6684 3.8664 13.6084 3.83967 13.5416 3.83967C13.4748 3.83967 13.4146 3.8664 13.3611 3.91988L12.8068 4.49509L12.0407 3.72904L12.6272 3.12175C12.8815 2.86745 13.1876 2.74029 13.5455 2.74029C13.9034 2.74029 14.2095 2.86745 14.4639 3.12175L16.898 5.55592C17.1523 5.81022 17.2795 6.11286 17.2795 6.46384C17.2795 6.81481 17.1523 7.11745 16.898 7.37175L16.2907 7.97904ZM7.26677 17.003C7.10969 17.1601 6.88344 17.2664 6.58802 17.322C6.2926 17.3775 6.01774 17.2781 5.76344 17.0238L3.19469 14.4549C2.93497 14.1995 2.8051 13.8838 2.8051 13.5078C2.8051 13.1318 2.93497 12.814 3.19469 12.5543L3.74906 12.0207L4.5151 12.7868L3.96865 13.3203C3.91517 13.3738 3.88844 13.4339 3.88844 13.5007C3.88844 13.5675 3.91517 13.6276 3.96865 13.6809L6.31802 16.0511C6.37135 16.1046 6.43142 16.1313 6.49823 16.1313C6.56503 16.1313 6.62517 16.1046 6.67865 16.0511L7.23302 15.5047L7.99906 16.2707L7.26677 17.003ZM15.1866 10.9807L16.2955 9.89259C16.349 9.83911 16.3757 9.77765 16.3757 9.70821C16.3757 9.63877 16.349 9.57731 16.2955 9.52384L10.5168 3.74509C10.4633 3.69161 10.4018 3.66488 10.3324 3.66488C10.263 3.66488 10.2015 3.69161 10.148 3.74509L9.03906 4.83321C8.98573 4.88668 8.95906 4.94675 8.95906 5.01342C8.95906 5.08022 8.98573 5.14036 9.03906 5.19384L14.8259 10.9807C14.8794 11.034 14.9395 11.0607 15.0064 11.0607C15.073 11.0607 15.1331 11.034 15.1866 10.9807ZM9.9126 16.2755L11.0007 15.1953C11.0541 15.142 11.0807 15.0819 11.0807 15.0151C11.0807 14.9483 11.0541 14.8882 11.0007 14.8349L5.20573 9.03988C5.1524 8.98654 5.09233 8.95988 5.02552 8.95988C4.95872 8.95988 4.89865 8.98654 4.84531 9.03988L3.7651 10.128C3.71163 10.1815 3.6849 10.2429 3.6849 10.3124C3.6849 10.3818 3.71163 10.4433 3.7651 10.4968L9.54385 16.2755C9.59733 16.329 9.65878 16.3557 9.72823 16.3557C9.79767 16.3557 9.85913 16.329 9.9126 16.2755ZM9.57594 11.8493L11.8822 9.55592L10.4639 8.15842L8.17052 10.4438L9.57594 11.8493ZM10.6705 17.0495C10.4109 17.3092 10.0968 17.439 9.72823 17.439C9.35962 17.439 9.04552 17.3092 8.78594 17.0495L2.99115 11.2338C2.73142 10.9743 2.60156 10.6636 2.60156 10.302C2.60156 9.94029 2.73142 9.62967 2.99115 9.37008L4.07927 8.27384C4.33469 8.01425 4.65038 7.88446 5.02635 7.88446C5.40247 7.88446 5.72031 8.01425 5.9799 8.27384L7.40448 9.67779L9.6899 7.37175L8.2651 5.95988C8.00552 5.70029 7.87573 5.38481 7.87573 5.01342C7.87573 4.64217 8.00552 4.32675 8.2651 4.06717L9.38219 2.97113C9.63761 2.7114 9.9533 2.58154 10.3293 2.58154C10.7052 2.58154 11.023 2.7114 11.2826 2.97113L17.0903 8.73717C17.35 8.99259 17.4799 9.30828 17.4799 9.68425C17.4799 10.0602 17.35 10.378 17.0903 10.6376L15.9734 11.7338C15.7139 11.9934 15.3984 12.1232 15.0272 12.1232C14.6558 12.1232 14.3403 11.9934 14.0807 11.7338L12.6272 10.309L10.342 12.6153L11.7668 14.0399C12.0264 14.2995 12.1596 14.6163 12.1666 14.9903C12.1735 15.3642 12.0472 15.6809 11.7876 15.9405L10.6705 17.0495Z" fill="var(--inverse-color)"/>
                    </g>
                  </svg>
                </div>
                <input
                  className="no-arrows-input weight-input"
                  style={{ width: getInputWidth(weight) }}
                  value={kr.weight}
                  placeholder="0"
                  onChange={(e) => {
                    changeKeyResultWeight(index, e.target.value);
                  }}
                  onBlur={() => handleUpdate()}
                />
                %
              </div>
              {krErrors[index]?.weight && (
                <p className="form-error">{t("objective_card.weight_error")}</p>
              )}
            </div>
          </div>
          <DndProvider backend={HTML5Backend}>
          <div className="initiatives-container">
            {kr.initiatives.map((init, initIndex) => (
              <DraggableInitiative
              key={init.id ? `init-${init.id}` : `init-${initIndex}`}
              index={initIndex}
              initiative={init}
              moveInitiative={(fromIndex, toIndex) => moveInitiative(index, fromIndex, toIndex)}
            >
              <div 
                className="initiative-header edit-initiative-item" 
                key={init.id ? `init-${init.id}` : `init-${initIndex}`} 
                style={{'--after-content': `"${init.weight}"`}}
                >
                <div ref={initiativeRef} onClick={() => setActiveInitiativeIndex(initIndex)}>
                  <AutoResizeTextarea
                    key={`init-${init.id}` || `init-${initIndex}`}
                    value={init.title}
                    onChange={(newTitle) => {
                      const newKeyResults = [...keyResults];
                      newKeyResults[index].initiatives[initIndex].title = newTitle;
                      setKeyResults(newKeyResults);
                    }}
                    onBlur={() => handleUpdate()}
                    placeholder={t("objective_card.initiative_placeholder", {number: initIndex + 1})}
                    className="initiative-text highlight-on-hover"
                  />
                {activeInitiativeIndex === initIndex && 
                  <div>                    
                    {[1, 2, 3, 4].map((currentWeight) => (
                      <span
                        className="weight-icon"
                        key={currentWeight}
                        onClick={() => {
                            setActiveInitiativeWeight(currentWeight);
                            const newKeyResults = [...keyResults];
                            newKeyResults[index].initiatives[initIndex].weight = currentWeight;
                            setKeyResults(newKeyResults);
                            handleUpdate();
                          }  
                        }
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <mask id="mask0_2_1843" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                          <rect width="24" height="24" fill="#D9D9D9"/>
                          </mask>
                          <g mask="url(#mask0_2_1843)">                        
                          {currentWeight <= keyResults[index].initiatives[initIndex].weight ? (
                              <path fillRule="evenodd" clipRule="evenodd" d="M19.5505 8.89909L20.0262 9.37485L20.705 8.7211C21.0435 8.3826 21.2127 7.96944 21.2127 7.4816C21.2127 6.99377 21.0435 6.5806 20.705 6.2421L17.759 3.2961C17.4205 2.9576 17.0031 2.78418 16.507 2.77585C16.0108 2.76752 15.5935 2.9326 15.255 3.2711L14.6012 3.94985L15.3257 4.67432L15.3256 4.67444L13.7165 3.06535C13.3716 2.72035 12.9457 2.54785 12.4387 2.54785C11.9317 2.54785 11.5058 2.72035 11.161 3.06535L9.87047 4.3556C9.52563 4.70043 9.35322 5.12894 9.35322 5.6411C9.35322 6.15344 9.52563 6.58202 9.87047 6.92685L11.5802 8.6211L8.63772 11.5634L6.92822 9.8536C6.58338 9.50877 6.15739 9.33635 5.65022 9.33635C5.14322 9.33635 4.7173 9.50877 4.37247 9.8536L3.09172 11.1441C2.74672 11.4889 2.57422 11.9159 2.57422 12.4249C2.57422 12.9339 2.74672 13.3608 3.09172 13.7056L4.45445 15.0683L4.45055 15.0742L3.97622 14.5999L3.36097 15.2151C3.01597 15.5601 2.84347 15.9861 2.84347 16.4931C2.84347 17.0001 3.01597 17.426 3.36097 17.7709L6.26847 20.6786C6.60697 21.0171 7.02013 21.1864 7.50797 21.1864C7.9958 21.1864 8.40897 21.0171 8.74747 20.6786L9.40122 20.0249L8.93818 19.5618L8.94306 19.5569L10.2955 20.9094C10.6403 21.2544 11.0672 21.4269 11.5762 21.4269C12.0852 21.4269 12.5121 21.2544 12.857 20.9094L14.1475 19.6286C14.4923 19.2838 14.6647 18.8579 14.6647 18.3509C14.6647 17.8437 14.4923 17.4177 14.1475 17.0729L12.4377 15.3634L15.38 12.4209L17.0742 14.1306C17.4191 14.4754 17.8476 14.6479 18.36 14.6479C18.8721 14.6479 19.3006 14.4754 19.6455 14.1306L20.9357 12.8401C21.2807 12.4953 21.4532 12.0694 21.4532 11.5624C21.4532 11.0554 21.2807 10.6294 20.9357 10.2846L19.5504 8.89925L19.5505 8.89909Z" fill="#664499"/>
                          ) : (
                            <path d="M20.0262 9.37485L18.982 8.3306L19.5972 7.69035C19.6614 7.62619 19.6935 7.55402 19.6935 7.47385C19.6935 7.39369 19.6614 7.3216 19.5972 7.2576L16.7435 4.40385C16.6795 4.33968 16.6074 4.3076 16.5272 4.3076C16.4471 4.3076 16.3749 4.33968 16.3107 4.40385L15.6705 5.0191L14.6012 3.94985L15.255 3.2711C15.5935 2.9326 16.0108 2.76752 16.507 2.77585C17.0031 2.78418 17.4205 2.9576 17.759 3.2961L20.705 6.2421C21.0435 6.5806 21.2127 6.99377 21.2127 7.4816C21.2127 7.96944 21.0435 8.3826 20.705 8.7211L20.0262 9.37485ZM8.74747 20.6786C8.40897 21.0171 7.9958 21.1864 7.50797 21.1864C7.02013 21.1864 6.60697 21.0171 6.26847 20.6786L3.36097 17.7709C3.01597 17.426 2.84347 17.0001 2.84347 16.4931C2.84347 15.9861 3.01597 15.5601 3.36097 15.2151L3.97622 14.5999L5.04547 15.6691L4.41472 16.2844C4.35055 16.3485 4.31847 16.4207 4.31847 16.5009C4.31847 16.581 4.35055 16.6531 4.41472 16.7171L7.28397 19.5864C7.34797 19.6505 7.42005 19.6826 7.50022 19.6826C7.58039 19.6826 7.65255 19.6505 7.71672 19.5864L8.33197 18.9556L9.40122 20.0249L8.74747 20.6786ZM18.5762 13.0769L19.857 11.7961C19.9211 11.7319 19.9532 11.6582 19.9532 11.5749C19.9532 11.4915 19.9211 11.4178 19.857 11.3536L12.6475 4.1441C12.5833 4.07994 12.5096 4.04785 12.4262 4.04785C12.3429 4.04785 12.2691 4.07994 12.205 4.1441L10.9242 5.42485C10.8602 5.48902 10.8282 5.5611 10.8282 5.6411C10.8282 5.72127 10.8602 5.79344 10.9242 5.8576L18.1435 13.0769C18.2076 13.1409 18.2798 13.1729 18.36 13.1729C18.44 13.1729 18.5121 13.1409 18.5762 13.0769ZM11.7975 19.8556L13.0782 18.5594C13.1422 18.4954 13.1742 18.4233 13.1742 18.3431C13.1742 18.2629 13.1422 18.1909 13.0782 18.1269L5.87422 10.9229C5.81022 10.8589 5.73814 10.8269 5.65797 10.8269C5.5778 10.8269 5.50572 10.8589 5.44172 10.9229L4.14547 12.2036C4.0813 12.2678 4.04922 12.3415 4.04922 12.4249C4.04922 12.5082 4.0813 12.5819 4.14547 12.6461L11.355 19.8556C11.4191 19.9198 11.4929 19.9519 11.5762 19.9519C11.6596 19.9519 11.7333 19.9198 11.7975 19.8556ZM11.3685 14.2941L14.311 11.3671L12.634 9.6901L9.70697 12.6326L11.3685 14.2941ZM12.857 20.9094C12.5121 21.2544 12.0852 21.4269 11.5762 21.4269C11.0672 21.4269 10.6403 21.2544 10.2955 20.9094L3.09172 13.7056C2.74672 13.3608 2.57422 12.9339 2.57422 12.4249C2.57422 11.9159 2.74672 11.4889 3.09172 11.1441L4.37247 9.8536C4.7173 9.50877 5.14322 9.33635 5.65022 9.33635C6.15739 9.33635 6.58338 9.50877 6.92822 9.8536L8.63772 11.5634L11.5802 8.6211L9.87047 6.92685C9.52563 6.58202 9.35322 6.15344 9.35322 5.6411C9.35322 5.12894 9.52563 4.70043 9.87047 4.3556L11.161 3.06535C11.5058 2.72035 11.9317 2.54785 12.4387 2.54785C12.9457 2.54785 13.3716 2.72035 13.7165 3.06535L20.9357 10.2846C21.2807 10.6294 21.4532 11.0554 21.4532 11.5624C21.4532 12.0694 21.2807 12.4953 20.9357 12.8401L19.6455 14.1306C19.3006 14.4754 18.8721 14.6479 18.36 14.6479C17.8476 14.6479 17.4191 14.4754 17.0742 14.1306L15.38 12.4209L12.4377 15.3634L14.1475 17.0729C14.4923 17.4177 14.6647 17.8437 14.6647 18.3509C14.6647 18.8579 14.4923 19.2838 14.1475 19.6286L12.857 20.9094Z" fill="#664499"/>
                          )}
                          </g>
                        </svg>
                        </span>
                    ))}
                  </div>
                }
              </div>
              <button className="delete-button" onClick={() => deleteInitiative(index, initIndex)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.33333 13.2292L10 11.5625L11.6667 13.2292L12.7292 12.1667L11.0625 10.5L12.7292 8.83333L11.6667 7.77083L10 9.4375L8.33333 7.77083L7.27083 8.83333L8.9375 10.5L7.27083 12.1667L8.33333 13.2292ZM6.5 17C6.0875 17 5.73438 16.8531 5.44062 16.5594C5.14687 16.2656 5 15.9125 5 15.5V5.5H4V4H8V3H12V4H16V5.5H15V15.491C15 15.9137 14.8531 16.2708 14.5594 16.5625C14.2656 16.8542 13.9125 17 13.5 17H6.5ZM13.5 5.5H6.5V15.5H13.5V5.5Z"/>
                </svg>
              </button>
              </div>
              </DraggableInitiative>
            ))}
            <div 
              className={`button addButton`}
              onClick={() => addInitiative(index)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.25 17V10.75H3V9.25H9.25V3H10.75V9.25H17V10.75H10.75V17H9.25Z" fill="white"/>
              </svg>
              {t("objective_card.add_initiative")}
            </div>
          </div>
          </DndProvider>
        </div>
      ))}
      <hr className="devider"/>
      <div className="addButton-wrapper">
        <div
          className="button addButton"
          onClick={toggleKrDropdown}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
            <path d="M9.25 17V10.75H3V9.25H9.25V3H10.75V9.25H17V10.75H10.75V17H9.25Z" fill="white"/>
          </svg>
          {t("objective_card.add_kr")}
        </div>

        {krDropdownOpen && (
          <div className="add-btn-dropdown kr-dropdown">
            <div className="dropdown-option" onClick={() => {addKeyResult("numeric"); setKrDropdownOpen(false);}}>
              {t("objective_card.type_numeric")}
            </div>
            <div className="dropdown-option" onClick={() => {addKeyResult("descriptive"); setKrDropdownOpen(false);}}>
              {t("objective_card.type_qualitative")}
            </div>
          </div>
        )}
      </div>  
      <hr className="devider"/>
      <button 
        className="button delete-objective-button"
        onClick={() => deleteObjective(objective.id)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="15" viewBox="0 0 18 15" fill="none">
          <path d="M0 15L9 0L18 15H0ZM2.64583 13.5H15.3542L9 2.91667L2.64583 13.5ZM8.99563 12.5C9.20688 12.5 9.38542 12.4285 9.53125 12.2856C9.67708 12.1427 9.75 11.9656 9.75 11.7544C9.75 11.5431 9.67854 11.3646 9.53563 11.2188C9.39271 11.0729 9.21563 11 9.00438 11C8.79313 11 8.61458 11.0715 8.46875 11.2144C8.32292 11.3573 8.25 11.5344 8.25 11.7456C8.25 11.9569 8.32146 12.1354 8.46438 12.2812C8.60729 12.4271 8.78438 12.5 8.99563 12.5ZM8.25 10H9.75V6H8.25V10Z" fill="#664499"/>
        </svg>
        {t("objective_card.delete_objective")}
      </button>
    </div>
  );
};

export default ObjectiveCard;